import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import api from "../../services/api";

export default function useTags({ companyId, socket }) {
  const [query, setQuery] = useState({ searchParam: "", pageNumber: 1 });
  const [search, setSearch] = useState("");
  const [result, setResult] = useState({ tags: [], count: 0, hasMore: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [revision, setRevision] = useState(0);
  const sequence = useRef(0);
  const inFlight = useRef(false);
  const refreshPending = useRef(false);
  const lastQuery = useRef(null);
  const refresh = useCallback(() => {
    if (inFlight.current) refreshPending.current = true;
    else setRevision(value => value + 1);
  }, []);
  useEffect(() => {
    const timer = setTimeout(() => setQuery(current => current.searchParam === search.trim() ? current : { searchParam: search.trim(), pageNumber: 1 }), 350);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    const request = ++sequence.current;
    const cancel = axios.CancelToken.source();
    const queryKey = JSON.stringify([companyId, query]);
    if (lastQuery.current !== queryKey) {
      setResult({ tags: [], count: 0, hasMore: false });
      lastQuery.current = queryKey;
    }
    inFlight.current = true;
    refreshPending.current = false;
    setLoading(true);
    setError(false);
    api.get("/tags", { params: { ...query, kanban: 0 }, cancelToken: cancel.token, timeout: 20000 }).then(({ data }) => {
      if (sequence.current !== request) return;
      const count = Number(data.count) || 0;
      const lastPage = Math.max(1, Math.ceil(count / 20));
      if (query.pageNumber > lastPage) {
        setQuery(current => ({ ...current, pageNumber: lastPage }));
        return;
      }
      setResult({ tags: data.tags, count, hasMore: Boolean(data.hasMore) });
    }).catch(err => {
      if (sequence.current === request && !axios.isCancel(err)) setError(true);
    }).finally(() => {
      if (sequence.current !== request) return;
      inFlight.current = false;
      setLoading(false);
      if (refreshPending.current) { refreshPending.current = false; setRevision(value => value + 1); }
    });
    return () => { sequence.current += 1; inFlight.current = false; cancel.cancel(); };
  }, [companyId, query, revision]);
  useEffect(() => {
    if (!socket || !companyId) return undefined;
    let timer;
    const changed = data => {
      if (!["create", "update", "delete"].includes(data?.action) || Number(data.tag?.kanban) === 1) return;
      if (!timer) timer = setTimeout(() => { timer = null; refresh(); }, 450);
    };
    const channel = `company${companyId}-tag`;
    socket.on(channel, changed);
    return () => { clearTimeout(timer); socket.off(channel, changed); };
  }, [companyId, socket, refresh]);
  const setPage = useCallback(pageNumber => setQuery(current => ({ ...current, pageNumber })), []);
  return { ...result, ...query, search, setSearch, setPage, loading, error, refresh };
}
